import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useRedeem } from '../../hooks/useRedeem';
import { BOOK_TITLE } from '../../lib/constants';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';

export function RedeemForm() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const { redeem, loading, error, success, message } = useRedeem();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && code) redeem(email, code);
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh-12rem)] bg-bg flex items-center justify-center px-4 py-16">
        <motion.div
          className="max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl font-bold text-text mb-2">
              Redeem Your Code
            </h1>
            <p className="text-text-light">
              Enter your email and redemption code to download{' '}
              <strong className="text-text">{BOOK_TITLE}</strong>.
            </p>
          </div>

          {success ? (
            <motion.div
              className="bg-sage/10 rounded-xl p-8 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <svg aria-hidden="true" className="w-12 h-12 text-sage mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sage-dark font-medium text-lg">{message}</p>
              <p className="text-text-light mt-2 text-sm">
                Your download link has been sent to your email.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-sage/10 p-8 shadow-sm space-y-5">
              <div>
                <label htmlFor="redeem-email" className="block text-sm font-medium text-text mb-1.5">
                  Email Address
                </label>
                <input
                  id="redeem-email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-sage/30 bg-white text-text placeholder:text-text-light/50 focus:outline-none focus:ring-2 focus:ring-sage/50 transition"
                />
              </div>

              <div>
                <label htmlFor="redeem-code" className="block text-sm font-medium text-text mb-1.5">
                  Redemption Code
                </label>
                <input
                  id="redeem-code"
                  type="text"
                  required
                  placeholder="MSKY-XXXX-XXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 rounded-lg border border-sage/30 bg-white text-text placeholder:text-text-light/50 focus:outline-none focus:ring-2 focus:ring-sage/50 transition font-mono tracking-wider"
                  maxLength={14}
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 rounded-lg bg-sage text-white font-semibold hover:bg-sage-dark disabled:opacity-60 transition-colors"
              >
                {loading ? 'Verifying...' : 'Redeem Code'}
              </button>
            </form>
          )}

          <p className="text-center mt-6">
            <Link to="/" className="text-sage-dark hover:text-sage text-sm transition-colors">
              &larr; Back to home
            </Link>
          </p>
        </motion.div>
      </div>
      <Footer />
    </>
  );
}
